import {useEffect, useRef, useState} from 'react';
import {client} from '../lib/sanity.js';
import MediaItem from './MediaItem.jsx';
import MediaItemPlaceholder from './MediaItemPlaceholder.jsx';

export default function RecruitmentSite() {
    const [isVisible, setIsVisible] = useState({});
    const [activeSlide, setActiveSlide] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isLargeScreen, setIsLargeScreen] = useState(false);
    const [mediaItems, setMediaItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const minSwipeDistance = 50;
    const wheelTimeout = useRef(null);
    const wheelDelta = useRef(0);
    const carouselRef = useRef(null);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsLargeScreen(window.innerWidth >= 960);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Placeholder data for loading state
    const placeholderItems = [
        {type: 'image', placeholder: 'Action Shot 1 - Diving Save'},
        {type: 'image', placeholder: 'Action Shot 2 - Distribution'},
        {type: 'image', placeholder: 'Action Shot 3 - High Claim'},
        {type: 'image', placeholder: 'Action Shot 4 - One-on-One'},
        {type: 'video', placeholder: 'Training Clip'},
    ];

    // Fetch media items from Sanity CMS
    useEffect(() => {
        const fetchMediaItems = async () => {
            setIsLoading(true);
            try {
                const query = '*[_type == "mediaItem" && order >= 0] | order(order desc)';
                const data = await client.fetch(query);
                setMediaItems(data);
                setIsLoading(false);
            } catch (err) {
                console.error('Error fetching media items:', err);
                setError(err.message || 'Failed to load media');
                setIsLoading(false);
            }
        };

        fetchMediaItems();
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({
                            ...prev,
                            [entry.target.id]: true
                        }));
                    }
                });
            },
            {threshold: 0.1}
        );

        document.querySelectorAll('.animate-section').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe) {
            setActiveSlide((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
        } else if (isRightSwipe) {
            setActiveSlide((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
        }
    };

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        const onWheel = (e) => {
            // Only handle horizontal swipes (two-finger trackpad gestures)
            if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
            if (Math.abs(e.deltaX) < 5) return;

            e.preventDefault();
            wheelDelta.current += e.deltaX;

            // Clear existing timeout
            if (wheelTimeout.current) {
                clearTimeout(wheelTimeout.current);
            }

            // Debounce and trigger slide change when threshold reached
            wheelTimeout.current = setTimeout(() => {
                if (wheelDelta.current > minSwipeDistance) {
                    setActiveSlide((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
                } else if (wheelDelta.current < -minSwipeDistance) {
                    setActiveSlide((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
                }
                wheelDelta.current = 0;
            }, 50);
        };

        carousel.addEventListener('wheel', onWheel, {passive: false});
        return () => carousel.removeEventListener('wheel', onWheel);
    }, [mediaItems.length]);

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({behavior: 'smooth'});
        setMenuOpen(false);
    };

    return (
        <div
            className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white font-sans">
            {/* Navigation */}
            <nav
                className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-amber-500/20">
                <div
                    className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
                    <div
                        className="text-amber-400 font-bold tracking-wider text-lg">MAX
                    </div>
                    <div className="hidden md:flex gap-8 text-sm tracking-wide">
                        {['About', 'Coaching', 'Media', 'Contact'].map((item) => (
                            <button
                                key={item}
                                onClick={() => scrollTo(item.toLowerCase())}
                                className="text-zinc-400 hover:text-amber-400 transition-colors duration-300"
                            >
                                {item.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <button className="md:hidden text-amber-400"
                            onClick={() => setMenuOpen(!menuOpen)}>
                        <svg className="w-6 h-6" fill="none"
                             stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  strokeWidth={2}
                                  d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}/>
                        </svg>
                    </button>
                </div>
                {menuOpen && (
                    <div
                        className="md:hidden bg-zinc-900 border-t border-amber-500/20 px-4 py-4 flex flex-col gap-4">
                        {['About', 'Coaching', 'Media', 'Contact'].map((item) => (
                            <button
                                key={item}
                                onClick={() => scrollTo(item.toLowerCase())}
                                className="text-zinc-400 hover:text-amber-400 transition-colors text-left"
                            >
                                {item.toUpperCase()}
                            </button>
                        ))}
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section id="about"
                     className="min-h-screen flex flex-col justify-center items-center pt-20 px-4 relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-900/10 via-transparent to-transparent"/>
                <div
                    className="absolute top-20 left-10 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl"/>
                <div
                    className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"/>

                <div className="text-center relative z-10 animate-section"
                     id="hero-content">
                    <div
                        className={`transition-all duration-1000 ${isVisible['hero-content'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

                        {/* Headshot */}
                        <div className="flex justify-center mb-8">
                            <div className="relative group w-48 h-48">
                                <div className="text-center">
                                    <img src="/assets/max_headshot_med.png"
                                         alt="Headshot of Max Oravecz"
                                         className="w-full h-full object-cover rounded-full"/>
                                </div>
                            </div>
                        </div>

                        <p className="text-amber-400 tracking-[0.3em] text-sm mb-4 font-medium">GOALKEEPER
                            | CLASS OF 2029</p>
                        <h1 className="text-6xl md:text-8xl font-black tracking-tight mb-2">
                            <span
                                className="bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">MAX</span>
                        </h1>
                        <h1 className="text-6xl md:text-8xl font-black tracking-tight mb-8">
                            <span
                                className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent">ORAVECZ</span>
                        </h1>

                        {/* Stats Grid */}
                        <div
                            className="grid grid-cols-2 gap-4 max-w-3xl mx-auto mb-12"
                            style={{gridTemplateColumns: isLargeScreen ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)'}}
                        >
                            {[
                                {label: 'HEIGHT', value: "5'9\""},
                                {label: 'WEIGHT', value: '145 lbs'},
                                {label: 'GPA', value: '3.65'},
                                {label: 'SAT/ACT', value: 'TBD'},
                            ].map((stat, i) => (
                                <div key={i}
                                     className="bg-zinc-800/50 backdrop-blur border border-zinc-700/50 rounded-lg p-4 hover:border-amber-500/50 transition-all duration-300 hover:scale-105">
                                    <p className="text-zinc-500 text-xs tracking-wider mb-1">{stat.label}</p>
                                    <p className="text-xl font-bold text-white">{stat.value}</p>
                                </div>
                            ))}
                        </div>

                        <div
                            className="bg-zinc-800/30 backdrop-blur border border-zinc-700/50 rounded-lg p-4 max-w-md mx-auto mb-12">
                            <p className="text-zinc-400 text-sm">
                                <span className="text-amber-400 font-semibold">Revere High School</span> •
                                Richfield, Ohio
                            </p>
                            <p className="text-zinc-500 text-xs mt-1">
                                Graduation: 2029</p>
                        </div>
                    </div>
                </div>

                {/* Video Section */}
                <div
                    className={`w-full max-w-4xl mx-auto px-4 relative z-10 animate-section transition-all duration-1000 delay-300 ${isVisible['hero-content'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    id="video-section">
                    <div
                        className="relative rounded-2xl overflow-hidden border border-amber-500/30 shadow-2xl shadow-amber-500/10">
                        <div
                            className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-transparent to-amber-500/20 rounded-2xl blur"/>
                        <div className="relative aspect-video bg-zinc-900">
                            <iframe
                                    className="w-full h-full"
                                    src="https://www.youtube.com/embed/P7qV1GyCxPY?si=8TUGA46WfB4ZWw9U"
                                    title="YouTube video player" frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen></iframe>
                        </div>
                    </div>
                </div>

                <div
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <svg className="w-6 h-6 text-amber-400/50" fill="none"
                         stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                    </svg>
                </div>
            </section>

            {/* Coaching Section */}
            <section id="coaching" className="py-24 px-4 animate-section">
                <div
                    className={`max-w-4xl mx-auto transition-all duration-1000 ${isVisible['coaching'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <p className="text-amber-400 tracking-[0.3em] text-sm mb-2 text-center">DEVELOPMENT</p>
                    <h2 className="text-4xl md:text-5xl font-black text-center mb-16 bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">
                        COACHING & TEAMS
                    </h2>

                    <div
                        className="flex flex-col gap-6 w-full"
                        style={{
                            flexDirection: isLargeScreen ? 'row' : 'column',
                            justifyContent: isLargeScreen ? 'center' : 'flex-start',
                            alignItems: isLargeScreen ? 'stretch' : 'center'
                        }}
                    >
                        {[
                            {
                                team: 'Revere High School',
                                role: 'Varsity Goalkeeper',
                                coach: 'Coach Nick DePompei',
                                icon: '🏫'
                            },
                            {
                                team: 'Cleveland Force SC',
                                role: 'Club Team',
                                coach: 'Coach Nick Felician',
                                icon: '⚽'
                            },
                            {
                                team: 'Ciornei Goalkeeping Academy',
                                role: 'Position Training',
                                coach: 'Coach Denny Ciornei',
                                icon: '🧤'
                            },
                        ].map((item, i) => (
                            <div
                                key={i}
                                className="group bg-zinc-800/30 backdrop-blur border border-zinc-700/50 rounded-xl p-6 hover:border-amber-500/50 transition-all duration-500 hover:scale-105 hover:bg-zinc-800/50"
                                style={{
                                    transitionDelay: `${i * 100}ms`,
                                    width: isLargeScreen ? '280px' : '100%',
                                    maxWidth: isLargeScreen ? '280px' : '320px',
                                    flex: isLargeScreen ? '0 0 280px' : 'none'
                                }}
                            >
                                <div className="text-4xl mb-4">{item.icon}</div>
                                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-amber-400 transition-colors">{item.team}</h3>
                                <p className="text-amber-400/80 text-sm mb-3">{item.role}</p>
                                <p className="text-zinc-400 text-sm">{item.coach}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Media Gallery Section */}
            <section id="media"
                     className="py-12 px-4 bg-zinc-900/50 animate-section">
                <div
                    className={`max-w-5xl mx-auto transition-all duration-1000 ${isVisible['media'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <p className="text-amber-400 tracking-[0.3em] text-sm mb-2 text-center">GALLERY</p>
                    <h2 className="text-4xl md:text-5xl font-black text-center mb-16 bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">
                        MEDIA
                    </h2>

                    {/* Carousel */}
                    <div className="relative">
                        {error ? (
                            // Error State
                            <div className="aspect-video flex items-center justify-center">
                                <p className="text-zinc-500 text-center">No media available</p>
                            </div>
                        ) : (
                            <>
                                <div
                                    ref={carouselRef}
                                    className="overflow-hidden rounded-xl"
                                    onTouchStart={onTouchStart}
                                    onTouchMove={onTouchMove}
                                    onTouchEnd={onTouchEnd}
                                >
                                    <div
                                        className="flex transition-transform duration-500 ease-out"
                                        style={{transform: `translateX(-${activeSlide * 100}%)`}}
                                    >
                                        {isLoading ? (
                                            // Loading State - Show placeholders
                                            placeholderItems.map((item, i) => (
                                                <MediaItemPlaceholder key={i} item={item} />
                                            ))
                                        ) : (
                                            // Loaded State - Show actual media
                                            mediaItems.map((item, index) => (
                                                <MediaItem key={item._id} item={item} isActive={activeSlide === index} />
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Carousel Controls - Hidden during error */}
                                <button
                                    onClick={() => setActiveSlide((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1))}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-zinc-800/80 backdrop-blur rounded-full flex items-center justify-center text-amber-400 hover:bg-amber-500 hover:text-zinc-900 transition-all"
                                >
                                    <svg className="w-5 h-5" fill="none"
                                         stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round"
                                              strokeLinejoin="round" strokeWidth={2}
                                              d="M15 19l-7-7 7-7"/>
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setActiveSlide((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1))}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-zinc-800/80 backdrop-blur rounded-full flex items-center justify-center text-amber-400 hover:bg-amber-500 hover:text-zinc-900 transition-all"
                                >
                                    <svg className="w-5 h-5" fill="none"
                                         stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round"
                                              strokeLinejoin="round" strokeWidth={2}
                                              d="M9 5l7 7-7 7"/>
                                    </svg>
                                </button>

                                {/* Dots - Hidden during error */}
                                <div className="flex justify-center gap-2 mt-6">
                                    {(isLoading ? placeholderItems : mediaItems).map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActiveSlide(i)}
                                            className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSlide === i ? 'bg-amber-400 w-6' : 'bg-zinc-600 hover:bg-zinc-500'}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-12 px-4 animate-section">
                <div
                    className={`max-w-2xl mx-auto transition-all duration-1000 ${isVisible['contact'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <p className="text-amber-400 tracking-[0.3em] text-sm mb-2 text-center">GET
                        IN TOUCH</p>
                    <h2 className="text-4xl md:text-5xl font-black text-center mb-16 bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">
                        CONTACT
                    </h2>

                    <div
                        className="bg-zinc-800/30 backdrop-blur border border-zinc-700/50 rounded-2xl p-8">
                        <div className="grid gap-6">
                            {/* Player Contact */}
                            <div>
                                <h3 className="text-amber-400 font-semibold mb-4 tracking-wide">PLAYER</h3>
                                <div className="space-y-3">
                                    <a href="mailto:keeper@maxoravecz.com"
                                       className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors group">
                                        <div
                                            className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                                            <svg className="w-5 h-5"
                                                 fill="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path
                                                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                            </svg>
                                        </div>
                                        <span>keeper@maxoravecz.com</span>
                                    </a>
                                    <a href="tel:+14405039507"
                                       className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors group">
                                        <div
                                            className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                                            <svg className="w-5 h-5"
                                                 fill="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path
                                                    d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                            </svg>
                                        </div>
                                        <span>(440) 503-9507</span>
                                    </a>
                                    <a href="https://youtube.com/@maxoravecz"
                                       target="_blank" rel="noopener noreferrer"
                                       className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors group">
                                        <div
                                            className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                                            <svg className="w-5 h-5"
                                                 fill="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path
                                                    d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                            </svg>
                                        </div>
                                        <span>@maxoravecz</span>
                                    </a>
                                </div>
                            </div>

                            <hr className="border-zinc-700/50"/>

                            {/* Parent Contact */}
                            <div>
                                <h3 className="text-amber-400 font-semibold mb-4 tracking-wide">PARENT/GUARDIAN</h3>
                                <div className="space-y-3">
                                    <p className="text-zinc-300 font-medium">Marc
                                        Oravecz</p>
                                    <a href="mailto:dad@maxoravecz.com"
                                       className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors group">
                                        <div
                                            className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                                            <svg className="w-5 h-5"
                                                 fill="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path
                                                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                            </svg>
                                        </div>
                                        <span>dad@maxoravecz.com</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 border-t border-zinc-800">
                <div
                    className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center font-black text-zinc-900">
                            MO
                        </div>
                        <div>
                            <p className="font-bold text-white">Max Oravecz</p>
                            <p className="text-zinc-500 text-sm">Class of
                                2029</p>
                        </div>
                    </div>
                    <p className="text-zinc-600 text-sm">Goalkeeper • Revere
                        High School</p>
                </div>
            </footer>
        </div>
    );
}
